import { formatSecondaryMOQName } from "~/utils/formatting"
import { AggregationMethod } from "~/components/reusable/AggregationSelector"
import { type IVariantSummary } from "../hooks/useVariantTableData"
import { filterDefined } from "~/utils/tsUtils"
import { addDays } from "date-fns"
import { isEqual, startCase, sumBy } from "lodash"
import { ProductTableColumn } from "~/constants/productTableColumns"
import {
  COLLECTION_TABLE_COLUMN_IDS,
  CollectionTableColumn,
  PerformanceLevel,
} from "~/constants/collections"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { SIZE_DIMENSION_1_LABEL, SIZE_DIMENSION_2_LABEL } from "~/utils/sizeDimensions"

export { ProductTableColumn }

/**
 * Return rate from pre-summed totals: returns / sales, or null on zero sales
 * (callers render "-" instead of NaN/Infinity).
 */
export function returnRateFromTotals(returns: number, sales: number): number | null {
  if (sales <= 0) return null
  return returns / sales
}

/**
 * A variant's preferred vendor, resolved to its name; the vendor id if the vendor object isn't loaded,
 * "Multiple vendors" if several are attached with none preferred, or undefined with no vendors at all.
 */
export function preferredVendorName(
  variant: Pick<IVariantSummary, "vendors" | "settings">,
): string | undefined {
  const preferred = variant.settings.preferredVendorId
  if (preferred) return variant.vendors.find((v) => v.id === preferred)?.name || preferred
  return variant.vendors.length ? "Multiple vendors" : undefined
}

/**
 * "Return rate" metric for the product table and variant card:
 * summed returns / summed sales across variants over the last 52 weeks.
 */
export function calcReturnRate(
  variants: Pick<IVariantSummary, "lastYearReturns" | "salesQuantity">[],
): number | null {
  return returnRateFromTotals(
    sumBy(variants, (v) => v.lastYearReturns ?? 0),
    sumBy(variants, (v) => v.salesQuantity ?? 0),
  )
}

/** A row's stockout records and the lead time that sets its forecast window. */
interface AvailabilityTodayRow {
  settings?: { totalLeadTimeDays?: number }
  outOfStockInformation?: ({ skuId: string; status: string } | undefined)[]
}

/** Forecast for a SKU over a lead-time window, as `availabilityTodayPercent` weights by. */
export type LeadTimeForecastFn = (skuId: string, totalLeadTimeDays: number) => number

/**
 * Quotes a SKU's forecast from the start of this week through `totalLeadTimeDays`, scoped to one
 * warehouse when a warehouse is selected. One accessor serves both the leaf and the group path,
 * so a group is the same computation over more rows.
 */
export function makeForecastOverLeadTime(
  getSkuForecastsBetweenDates: (
    skuId: string,
    warehouseIds: string[] | undefined,
    firstDate: Date | null,
    secondDate: Date | null,
  ) => number,
  warehouseId: string | undefined,
): LeadTimeForecastFn {
  return (skuId, totalLeadTimeDays) =>
    getSkuForecastsBetweenDates(
      skuId,
      warehouseId ? [warehouseId] : undefined,
      null,
      addDays(getStartOfCurrentWeekUTC(), totalLeadTimeDays),
    )
}

/**
 * "Availability today": the share of near-term demand not sitting on a SKU that is stocked out
 * right now, weighted by each SKU's forecast over the lead-time window.
 *
 * Not `availabilityWithinLeadTime`, which is the projection walk's residual: this one classifies
 * each SKU as wholly served or wholly lost by its current stockout status, so the two columns are
 * not expected to agree. Returns 0-100, or `null` when there is no forecast to weight by.
 *
 * Takes rows rather than SKU records so a group is the same computation over more rows: each row
 * is quoted over its own lead time, the weights accumulate across rows, and the percentage is
 * rounded once at the end. `forecastOverLeadTime`'s warehouse scope belongs to the caller; the
 * window is the lead time handed to it, and a row carrying none is quoted over zero days.
 */
export function availabilityTodayPercent(
  rows: readonly AvailabilityTodayRow[],
  forecastOverLeadTime: LeadTimeForecastFn,
): number | null {
  return availabilityTodayFromTotals(
    rows.map((row) => availabilityTodayTotals(row, forecastOverLeadTime)),
  )
}

/** One row's share of {@link availabilityTodayPercent}: its weighting forecast and the lost part. */
export interface AvailabilityTodayTotals {
  forecast: number
  lostForecast: number
}

export function availabilityTodayTotals(
  row: AvailabilityTodayRow,
  forecastOverLeadTime: LeadTimeForecastFn,
): AvailabilityTodayTotals {
  const totalLeadTimeDays = row.settings?.totalLeadTimeDays ?? 0
  let forecast = 0
  let lostForecast = 0
  for (const entry of filterDefined(row.outOfStockInformation)) {
    const skuForecast = forecastOverLeadTime(entry.skuId, totalLeadTimeDays)
    forecast += skuForecast
    if (entry.status === "stockout") lostForecast += skuForecast
  }
  return { forecast, lostForecast }
}

export function availabilityTodayFromTotals(
  totals: readonly AvailabilityTodayTotals[],
): number | null {
  let forecast = 0
  let lostForecast = 0
  for (const row of totals) {
    forecast += row.forecast
    lostForecast += row.lostForecast
  }
  if (!forecast) return null
  return Math.round((1 - lostForecast / forecast) * 100)
}

/**
 * Forecast-weighted mean of per-location availability. `null` when there is no forecast to weight
 * by — callers decide whether that reads as 0 or as no value.
 */
export function locationAvailability(
  items: { availability: number; thirteenWeeksForecast: number }[],
): number | null {
  const totalForecast = sumBy(items, "thirteenWeeksForecast")
  if (totalForecast === 0) return null
  return sumBy(items, (i) => i.availability * i.thirteenWeeksForecast) / totalForecast
}

/**
 * Checks if a column ID is a calendar column.
 * Calendar columns are dynamically generated time-period columns
 * (Week/Month/Quarter) used in calendar pages that should be:
 * - Always visible (not hideable via ColumnChooser)
 * - Not reorderable
 * - Excluded from saved views
 */
export function isCalendarColumn(colId: string): boolean {
  return colId.startsWith("calendar-")
}

/**
 * Sentinel colId used in saved `columnOrder.orderedColIds` to mark the position of
 * the calendar block. Each calendar table has its calendar columns expanded at
 * render time (week/month IDs change every reload); collapsing them to a single
 * sentinel on save preserves where the user positioned the block relative to
 * non-calendar columns without committing to ephemeral week IDs.
 */
export const CALENDAR_BLOCK_SENTINEL = "__calendar_block__"

/** A saved column-order entry that stands for the calendar block: the sentinel or a calendar column. */
export function isCalendarOrderEntry(colId: string): boolean {
  return colId === CALENDAR_BLOCK_SENTINEL || isCalendarColumn(colId)
}

/**
 * Stale column ID prefixes from old naming conventions before the calendar- prefix was added.
 * These patterns are used to migrate saved views and session storage to the new naming.
 */
const STALE_CALENDAR_COLUMN_PREFIXES = [
  // Incoming Orders Calendar
  "incoming-week-",
  "incoming-month-",
  "incoming-quarter-",
  // Forecast Calendar
  "sales-",
  "forecast-",
  // Inventory Calendar
  "inventory-",
  "future-closing-",
  // Availability Calendar (planning)
  "availabilityMonth-",
  "availabilityWeek-",
  // Purchase Plan Calendar
  "purchasePlanMonth-",
] as const

/**
 * Some grid column IDs are raw month display names (e.g. "August") instead of
 * real colIds, from a pre-calendar-prefix era. Treat them as stale so
 * `isStaleCalendarColumnId` filters them out of gridState column sizing/order.
 */
const STALE_MONTH_DISPLAY_NAMES = new Set([
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
])

/** Demand 2 column ids since removed; a saved view naming one drops it silently. */
const RETIRED_COLUMN_IDS: ReadonlySet<string> = new Set([
  "demand2-v-status",
  "demand2-v-sizes",
  "demand2-npi-actions",
  "demand2-npi-match",
  "demand2-npi-implied-baseline",
  "demand2-npi-match-baseline",
  "demand2-co-own-sales-baseline",
  "demand2-co-match-sales",
])

/** A column id a saved view may still name but no table builds: stale calendar or retired. */
export const isStaleColumnId = (id: string): boolean =>
  isStaleCalendarColumnId(id) || RETIRED_COLUMN_IDS.has(id)

/**
 * Checks if a column ID is a stale calendar column from old naming conventions.
 * Used to filter out deprecated column IDs when applying saved views.
 */
export function isStaleCalendarColumnId(id: string): boolean {
  return (
    (STALE_CALENDAR_COLUMN_PREFIXES.some((pattern) => id.startsWith(pattern)) &&
      !id.startsWith("calendar-")) ||
    STALE_MONTH_DISPLAY_NAMES.has(id)
  )
}

/**
 * Decides how a table's displayed columns should react when its default column set
 * changes (e.g. comparison columns toggled on/off, or data-derived columns resolving
 * from a query). Returns the new displayed ids, or null when nothing should change.
 *
 * - Displayed holds exactly the previous default's ids → follow the new default.
 * - Otherwise (customized selection or applied saved view) → merge the delta: drop
 *   removed defaults, append added ones so they group with their siblings. Ids in
 *   `excludedFromAutoAdd` are data-derived (they appear when backend data changes,
 *   not from a user action) and must never be auto-appended to a customized
 *   selection, otherwise they leak into saved views.
 */
export function resolveDisplayedColumnsOnDefaultChange(
  prevDefault: string[] | undefined,
  currDefault: string[],
  displayed: readonly string[],
  excludedFromAutoAdd: readonly string[] = [],
): string[] | null {
  if (!prevDefault || isEqual(prevDefault, currDefault)) return null
  const displayedSet = new Set(displayed)
  if (
    displayedSet.size === new Set(prevDefault).size &&
    prevDefault.every((id) => displayedSet.has(id))
  ) {
    return currDefault
  }

  const excluded = new Set(excludedFromAutoAdd)
  const added = currDefault.filter(
    (id) => !prevDefault.includes(id) && !excluded.has(id) && !displayedSet.has(id),
  )
  const removed = new Set(prevDefault.filter((id) => !currDefault.includes(id)))

  const next = [...displayed.filter((id) => !removed.has(id)), ...added]
  return isEqual(next, displayed) ? null : next
}

/** A table's default columns: `overwriteColumnsToShow`, all shown, when it sets no defaults. */
export function effectiveDefaultColumnIds(
  defaults: string[],
  overwriteColumnsToShow: string[] | undefined,
): string[] {
  return defaults.length > 0 ? defaults : (overwriteColumnsToShow ?? defaults)
}

const collectionTotalColumnIds: string[] = [
  CollectionTableColumn.Forecast,
  CollectionTableColumn.Sales,
  CollectionTableColumn.PreOrders,
]

const variantColumnIds: string[] = [
  ProductTableColumn.Sales,
  ProductTableColumn.Forecast,
  ProductTableColumn.AvailabilityToday,
]

const collectionTotalColumnIdSet = new Set(collectionTotalColumnIds)
// Suggested order is hidden for collection totals but not a default, so it isn't re-added.
const swappableColumnIds = new Set([
  ...collectionTotalColumnIds,
  ...variantColumnIds,
  ProductTableColumn.SuggestedOrder,
])

/**
 * The NOOS demand table's columns once the collection filter changes. With a filter and no
 * collection totals shown, the totals replace the variant columns; without a filter, the variant
 * columns replace the totals and every collection column is hidden. Otherwise `shown` is kept, so
 * an already-matching set is left alone.
 */
export function collectionFilterColumns(shown: string[], hasCollections: boolean): string[] {
  const showsTotals = shown.some((id) => collectionTotalColumnIdSet.has(id))
  const kept = shown.filter((id) => !swappableColumnIds.has(id))
  if (hasCollections) {
    return showsTotals ? shown : [...kept, ...collectionTotalColumnIds]
  }
  const swapped = showsTotals ? [...kept, ...variantColumnIds] : shown
  return swapped.filter((id) => !COLLECTION_TABLE_COLUMN_IDS.has(id))
}

// TODO: Why are some uppercase and some lowercase?
// TODO: Should be moved to constants/productTable.ts

export const detailedSkuColumns = [
  ProductTableColumn.AvailabilityToday,
  ProductTableColumn.OverstockQuantity,
  ProductTableColumn.RightMargin,
  ProductTableColumn.SuggestedOrder,
  ProductTableColumn.ColliSize,
  ProductTableColumn.Buffer,
  ProductTableColumn.WeeksOnHand,
  ProductTableColumn.Sales,
  ProductTableColumn.PreOrders,
  ProductTableColumn.PreOrdersLastYear,
  ProductTableColumn.Forecast,
  ProductTableColumn.StockLevel,
  ProductTableColumn.ReservedStockLevel,
  ProductTableColumn.AvailableToSell,
  ProductTableColumn.InventoryValue,
  ProductTableColumn.CostPrice,
  ProductTableColumn.LeadTime,
  ProductTableColumn.ServiceLevel,
  ProductTableColumn.SoldSince,
  ProductTableColumn.WeekCover,
  ProductTableColumn.SalesGrowth,
  ProductTableColumn.ForecastedGrowth,
  ProductTableColumn.PurchaseInterval,
  ProductTableColumn.IncomingStock,
  ProductTableColumn.LostSales,
  ProductTableColumn.Profit,
  ProductTableColumn.OutOfStockDate,
  ProductTableColumn.SafetyStockOutDate,
  ProductTableColumn.OptimalOrderDate,
  ProductTableColumn.StockStatus,
  ProductTableColumn.MinimumOrderQuantity,
  ProductTableColumn.SecondaryMOQ,
  ProductTableColumn.AvailabilityWithinLeadTime,
  ProductTableColumn.LocationAvailability,
  ProductTableColumn.SupplierSku,
  ProductTableColumn.ProductType,
  ProductTableColumn.SizeDimension1,
  ProductTableColumn.SizeDimension2,
  ProductTableColumn.SizeSplitConfidence,
  ProductTableColumn.SellThroughRate,
  ProductTableColumn.InventoryTurn,
  ProductTableColumn.GMROI,
  ProductTableColumn.Revenue,
  ProductTableColumn.Returns,
  ProductTableColumn.SalesLast30Days,
  ProductTableColumn.NetSales,
  "nextIncomingStock",
  "nextIncomingDate",
  "lastIncomingDate",
  "totalIncomingStock",
  "availabilityMonth-0",
  "availabilityMonth-1",
  "availabilityMonth-2",
  "availabilityMonth-3",
  "availabilityMonth-4",
  "availabilityMonth-5",
  "availabilityMonth-6",
  "availabilityMonth-7",
  "availabilityMonth-8",
  "availabilityMonth-9",
  "availabilityMonth-10",
  "availabilityMonth-11",
  "purchasePlanMonth-0",
  "purchasePlanMonth-1",
  "purchasePlanMonth-2",
  "purchasePlanMonth-3",
  "purchasePlanMonth-4",
  "purchasePlanMonth-5",
  "purchasePlanMonth-6",
  "purchasePlanMonth-7",
  "purchasePlanMonth-8",
  "purchasePlanMonth-9",
  "purchasePlanMonth-10",
  "purchasePlanMonth-11",
  "purchasePlanMonth-12",
  "purchasePlanMonth-13",
  "purchasePlanMonth-14",
]

export enum NpiTableColumn {
  ProductName = "productName",
  Variant = "variant",
  SKU = "sku",
  Brand = "brand",
  Category = "category",
  Gender = "gender",
  Color = "color",
  Size = "size",
  StockLevel = "stockLevel",
  IncomingStock = "incomingStock",
  Sales = "salesLast12Months",
  ProductType = "productType",
  Recommendation = "recommendation",
  Tags = "tags",
  Active = "isActive",
  Collections = "collections",
  ActivatedFrom = "activatedFrom",
  ActivatedUntil = "activatedUntil",
  PhasingOutFrom = "phasingOutFrom",
  RemovedFromErp = "removedFromErp",
}

/**
 * Formats a calendar column ID to a meaningful display name.
 * Converts IDs like "incoming-month-0" to "Incoming Month 1"
 * by replacing hyphens with spaces, using startCase, and converting 0-based index to 1-based.
 */
function formatCalendarColumnId(columnId: string): string | null {
  // Pattern to match calendar column IDs ending with a number
  // Matches: "incoming-month-0", "inventory-Week-5", "availabilityMonth-0", etc.
  const calendarPattern = /^(.+?)[-]?(\d+)$/

  const match = columnId.match(calendarPattern)
  if (!match) return null

  const prefix = match[1]
  const indexStr = match[2]
  if (!prefix || !indexStr) return null

  // Check if this looks like a calendar column (contains week/month/quarter)
  if (!/week|month|quarter/i.test(prefix)) return null

  const index = parseInt(indexStr, 10)
  const displayIndex = index + 1 // Convert 0-based to 1-based

  // Replace hyphens with spaces and apply startCase
  const formattedPrefix = startCase(prefix.replace(/-/g, " "))

  return `${formattedPrefix} ${displayIndex}`
}

export function formatColumnName(columnId: string) {
  const customLabels: Record<string, string> = {
    buffer: "Safety Stock",
    minimumOrderQuantity: "SKU MOQ",
    salesLast30Days: "Sales (Last 30 days)",
    salesQuantity: "Sales (Last 52 weeks)",
    netSalesQuantity: "Net Sales (Last 52 weeks)",
    forecastQuantity: "Forecast (Next 52 weeks)",
    [ProductTableColumn.ReturnRate]: "Return rate (last 52 weeks)",
    [ProductTableColumn.ReturnRateWholeHorizon]: "Return rate (full horizon)",
  }

  if (columnId === ProductTableColumn.SecondaryMOQ) {
    return formatSecondaryMOQName()
  }

  const label = customLabels[columnId]
  if (label) {
    return label
  }

  // Check if this is a calendar column ID
  const calendarLabel = formatCalendarColumnId(columnId)
  if (calendarLabel) {
    return calendarLabel
  }

  return columnId
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace("-", " ")
    .replace(/^\w/, (c) => c.toUpperCase())
}

export const getNewAggregations = (newAggregation: string) => {
  if (newAggregation === AggregationMethod.Variant) return [newAggregation]
  if (newAggregation === AggregationMethod.ProductName)
    return [newAggregation, AggregationMethod.Variant]
  return [newAggregation, AggregationMethod.ProductName, AggregationMethod.Variant]
}

/**
 * Effective default grouping stack. The company-wide default overrides a screen's
 * own default only when that default is a product/variant level
 * (ProductName/Variant/SKU/undefined); domain dimensions (Fabric/Vendor/etc.) are
 * never overridden.
 */
export function resolveDefaultAggregations(
  screenDefault: string | undefined,
  companyDefault: string | null | undefined,
): string[] {
  return resolveDefaultGrouping(screenDefault, companyDefault).aggregations
}

/** {@link resolveDefaultAggregations}, plus whether the company setting decided it. */
export function resolveDefaultGrouping(
  screenDefault: string | undefined,
  companyDefault: string | null | undefined,
): { aggregations: string[]; fromCompany: boolean } {
  const isLevelDefault =
    screenDefault === undefined ||
    screenDefault === AggregationMethod.ProductName ||
    screenDefault === AggregationMethod.Variant ||
    screenDefault === AggregationMethod.SKU
  if (companyDefault && isLevelDefault) {
    return { aggregations: getNewAggregations(companyDefault), fromCompany: true }
  }
  return {
    aggregations: getNewAggregations(screenDefault ?? AggregationMethod.ProductName),
    fromCompany: false,
  }
}

/** Row-grouping dimensions shared by the NOOS and fashion buy tables. */
export const BUY_GROUP_BY_VALUES = [
  "variant",
  "product",
  "color",
  "size",
  "sizeDimension1",
  "sizeDimension2",
  "vendor",
] as const
export type BuyGroupBy = (typeof BUY_GROUP_BY_VALUES)[number]

// `productLike` dimensions keep the rich product cell (image + name) in the group row;
// the rest render the plain group value.
export type GroupOption<T extends string> = { value: T; label: string; productLike: boolean }

export const BUY_GROUP_OPTIONS: GroupOption<BuyGroupBy>[] = [
  { value: "variant", label: "Variant", productLike: true },
  { value: "product", label: "Product", productLike: true },
  { value: "color", label: "Color", productLike: false },
  { value: "size", label: "Size", productLike: false },
  { value: "sizeDimension1", label: SIZE_DIMENSION_1_LABEL, productLike: false },
  { value: "sizeDimension2", label: SIZE_DIMENSION_2_LABEL, productLike: false },
  { value: "vendor", label: "Vendor", productLike: false },
]

/** The column sort fields that order a group-by's groups; empty sorts them A–Z. */
export type GroupSorting = { sortingField?: string; sortingType?: "size" | "sizeDimension" }

export const SIZE_GROUP_SORTING: GroupSorting = { sortingField: "size", sortingType: "size" }
export const ALPHA_GROUP_SORTING: GroupSorting = {}

/** How a group-by dimension orders its groups: size order, its own dimension order, or A–Z. */
export const getBuyGroupSorting = (groupBy: BuyGroupBy): GroupSorting => {
  if (groupBy === "sizeDimension1" || groupBy === "sizeDimension2") {
    return { sortingField: groupBy, sortingType: "sizeDimension" }
  }
  if (groupBy === "variant" || groupBy === "size") return SIZE_GROUP_SORTING
  return ALPHA_GROUP_SORTING
}

/**
 * Company default → buy/fashion group-by default. "product"/"variant" are always
 * available on the buy tables, so only "Product Name" flips them; everything else
 * (incl. null) keeps "variant".
 */
export const resolveDefaultBuyGroupBy = (
  companyDefault: string | null | undefined,
): "product" | "variant" =>
  companyDefault === AggregationMethod.ProductName ? "product" : "variant"

/**
 * Company default → Product Performance level. Product/Variant follow the setting;
 * everything else (incl. null) keeps Category.
 */
export const resolveDefaultPerformanceLevel = (
  companyDefault: string | null | undefined,
): PerformanceLevel =>
  companyDefault === AggregationMethod.ProductName
    ? PerformanceLevel.ProductId
    : companyDefault === AggregationMethod.Variant
      ? PerformanceLevel.VariantId
      : PerformanceLevel.Category

/**
 * Company default → collection table aggregation. Unlike resolveDefaultAggregations,
 * this deliberately overrides the Category fallback whenever a company level is set.
 */
export const resolveDefaultCollectionAggregation = (
  companyDefault: string | null | undefined,
): AggregationMethod =>
  companyDefault === AggregationMethod.ProductName || companyDefault === AggregationMethod.Variant
    ? companyDefault
    : AggregationMethod.Category

/**
 * Extracts variant summaries from a node or data property
 * @param p The object containing node or data property
 * @returns Array of variant summaries
 */
export const extractVariantSummaries = (p: any): IVariantSummary[] => {
  if (p.rowNode?.allLeafChildren) {
    return filterDefined(
      p.rowNode?.allLeafChildren?.map((node: any) => node.data as IVariantSummary),
    )
  } else if (p.node?.group) {
    return filterDefined(p.node?.allLeafChildren?.map((node: any) => node.data as IVariantSummary))
  } else if (p.data) {
    return [p.data]
  } else {
    return []
  }
}

/**
 * Validates aggregation methods from a saved view.
 * - Filters out invalid methods (must be in AggregationMethod enum or contain "extradata")
 * - Removes legacy "SKU" aggregation
 * - Falls back to ["Product Name", "Variant"] if any invalid methods found
 */
export function validateAndNormalizeAggregations(
  aggregationMethods: string[] | undefined,
): AggregationMethod[] {
  const methods = aggregationMethods ?? []
  const validMethods = methods.filter(
    (a) =>
      a.includes("extradata") ||
      (Object.values(AggregationMethod).includes(a as AggregationMethod) && a !== "SKU"),
  )

  // If all methods are valid, use them (minus SKU)
  if (validMethods.length === methods.length) {
    return methods.filter((a) => a !== "SKU") as AggregationMethod[]
  }

  // Fall back to defaults if any invalid methods found
  return ["Product Name", "Variant"] as AggregationMethod[]
}

export function withMoqColumn(
  baseColumns: ProductTableColumn[],
  variants: { settings?: { hasSkuMoqData?: boolean } | null }[],
): ProductTableColumn[] {
  const hasMoqData = variants.some((v) => v.settings?.hasSkuMoqData)
  return hasMoqData ? [...baseColumns, ProductTableColumn.MinimumOrderQuantity] : baseColumns
}

/** The planner-facing name for a variant's product type. */
export const translateProductType = (type: string | undefined | null) => {
  switch (type) {
    case "noos":
      return "NOOS"
    case "fashion":
      return "Fashion"
    default:
      return ""
  }
}
